import { track } from '@eventbrite/datalayer-library';
import { getDateForAttendee } from '@eventbrite/event-date-time';
import type { ComponentProps } from 'react';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { loadDatesForMonth } from '../../actions/eventDates';
import {
    setCollapsedSectionsHeight,
    setSection,
    setSelectedSessionId,
    setSelectedTime,
    setShouldShowTicketError,
    setShouldShowTimeError,
    type InfiniteCalendarSeriesDispatcher,
} from '../../actions/infiniteCalendarSeries';
import { HeapEventName } from '../../constants/heap/events';
import {
    getIsAbandonOrderAttemptPage,
    getOnCloseFn,
} from '../../containers/connectPage';
import { getTemplateProps } from '../../containers/template';
import { getAppLocale, getIsAppRebrandActive } from '../../selectors/app';
import { getEventSalesStatus, getIsEventOnline } from '../../selectors/event';
import {
    getActiveSection,
    getCalendarDates,
    getCalendarType,
    getCollapsedSectionsHeight,
    getCompactCalendarData,
    getFirstEventDateString,
    getFirstSessionStartDateTime,
    getIsTicketSectionLoading,
    getIsTimeSectionLoading,
    getParentEventInfo,
    getScrollCalendarData,
    getSelectedDate,
    getSelectedSessionId,
    getSelectedTime,
    getShouldShowTicketError,
    getShouldShowTimeError,
    getShouldShowTimeSection,
    getTimeSlots,
    getTimezone,
} from '../../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import type { CheckoutState } from '../../state';
import {
    AccordionSection,
    CalendarType,
    type InfiniteCalendarSeriesNamespace,
} from '../../state/infiniteCalendarSeries';
import { formatDateTime } from '../../utils/date';
import { normalizeLocale } from '../../utils/general';
import {
    handleDateSelect,
    handleTicketSelectionFlow,
} from './handleDateSelect';
import { InfiniteCalendarSeries } from './InfiniteCalendarSeries';
import {
    calcCollapsedSectionsHeight,
    getMonthAndYearForAttendee,
    updateCollapsedSectionsHeight,
} from './utils';

const createSharedPropsForHeap = (calendarType: CalendarType | undefined) => ({
    isSeriesInfiniteCalendar: true,
    calendarType,
});

export const mapStateToProps = (
    state: CheckoutState,
): InfiniteCalendarSeriesNamespace => {
    const locale = normalizeLocale(getAppLocale(state));
    const timezone = getTimezone(state);
    const isOnlineEvent = getIsEventOnline(state);
    const firstDateFromServerContext = getFirstEventDateString(state);
    const firstSessionStartDateTime = getFirstSessionStartDateTime(state);

    const date = getDateForAttendee({
        isOnlineEvent,
        dateAndTimezone: {
            utc: firstSessionStartDateTime,
            timezone,
        },
    }).toDate();

    return {
        activeSection: getActiveSection(state),
        calendarDates: getCalendarDates(state),
        calendarType: getCalendarType(state),
        compactCalendarData: getCompactCalendarData(state),
        collapsedSectionsHeight: getCollapsedSectionsHeight(state),
        eventDateOverride: formatDateTime(date, {
            locale: locale,
            timeZone: timezone,
            omitTime: true,
        }),
        eventStatus: getEventSalesStatus(state)?.eventSalesStatus?.messageCode,
        firstMonth: {
            ...getMonthAndYearForAttendee(
                isOnlineEvent,
                firstDateFromServerContext,
                timezone,
            ),
        },
        isAppRebrandActive: getIsAppRebrandActive(state),
        isEventOnline: isOnlineEvent,
        isTicketSectionLoading: getIsTicketSectionLoading(state),
        isTimeSectionLoading: getIsTimeSectionLoading(state),
        locale,
        parentEventInfo: getParentEventInfo(state),
        scrollCalendarData: getScrollCalendarData(state),
        selectedDate: getSelectedDate(state),
        selectedSessionId: getSelectedSessionId(state),
        selectedTime: getSelectedTime(state),
        shouldShowTicketError: getShouldShowTicketError(state),
        shouldShowTimeError: getShouldShowTimeError(state),
        shouldShowTimeSection: getShouldShowTimeSection(state),
        templateProps: getTemplateProps({ state, formName: '' }),
        timeSlots: getTimeSlots(state),
        timezone,
    };
};

export const mapDispatchToProps = (
    dispatch: Dispatch<CheckoutState>,
    ownProps: any,
): InfiniteCalendarSeriesDispatcher => {
    const { router } = ownProps;
    const isAbandonOrderAttemptPage = getIsAbandonOrderAttemptPage(router);

    return {
        onPageClose: getOnCloseFn(router, isAbandonOrderAttemptPage, dispatch),
        onDateSelect: (dateString, calendarType, id, sessionTime) => {
            dispatch(setSelectedTime(''));
            handleDateSelect({
                dateString,
                calendarType,
                eventId: id,
                sessionTime,
                dispatch,
                track,
            });
        },
        onTimeSelect: async (eventId, sessionLabel, calendarType) => {
            updateCollapsedSectionsHeight(dispatch, calendarType);
            const sharedProps = createSharedPropsForHeap(calendarType);
            track({
                eventName: HeapEventName.timedEntrySessionSelectorClick,
                eventData: {
                    selectedOption: sessionLabel,
                    ...sharedProps,
                },
            });
            dispatch(setShouldShowTicketError(false));
            dispatch(setSelectedTime(sessionLabel));
            await handleTicketSelectionFlow({ eventId, dispatch });
        },
        onMonthLoad: async (month, year) => {
            dispatch(loadDatesForMonth(month, year));
        },
        resetTimeSelection: () => {
            dispatch(setSelectedTime(''));
            dispatch(setSelectedSessionId(''));
        },
        onInitialLoad: () => {
            dispatch(setCollapsedSectionsHeight(calcCollapsedSectionsHeight()));
        },
        onToggleSection: ({
            activeSection,
            selectedDate,
            selectedTime,
            calendarType,
        }) => {
            updateCollapsedSectionsHeight(dispatch, calendarType);
            const sharedProps = createSharedPropsForHeap(calendarType);
            const isCompactWithSessionCalendar =
                calendarType === CalendarType.compactWithSession;
            const isSingleDayEvent =
                calendarType === CalendarType.singleDayEvents;

            if (isSingleDayEvent && !selectedTime) {
                dispatch(setShouldShowTicketError(true));
                return;
            }

            if (activeSection === AccordionSection.date) {
                dispatch(setSelectedTime(''));
                dispatch(setSelectedSessionId(''));
                track({
                    eventName:
                        HeapEventName.timedEntryDateAccordionSectionClick,
                    eventData: sharedProps,
                });
            } else if (activeSection === AccordionSection.time) {
                track({
                    eventName:
                        HeapEventName.timedEntrySessionAccordionSectionClick,
                    eventData: sharedProps,
                });
            }

            if (isCompactWithSessionCalendar) {
                if (
                    activeSection === AccordionSection.tickets &&
                    !selectedDate
                ) {
                    dispatch(setShouldShowTicketError(true));
                    return;
                }
                dispatch(setSection(activeSection));
                return;
            }

            if (activeSection === AccordionSection.time && !selectedDate) {
                dispatch(setShouldShowTimeError(true));
                dispatch(setShouldShowTicketError(false));
            } else if (
                activeSection === AccordionSection.tickets &&
                !selectedTime
            ) {
                dispatch(setShouldShowTicketError(true));
                dispatch(setShouldShowTimeError(false));
            } else {
                dispatch(setShouldShowTimeError(false));
                dispatch(setShouldShowTicketError(false));
                dispatch(setSection(activeSection));
            }
        },
    };
};

export const mergeProps = (
    stateProps: InfiniteCalendarSeriesNamespace,
    dispatchProps: InfiniteCalendarSeriesDispatcher,
    ownProps: ComponentProps<typeof InfiniteCalendarSeries>,
) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
});

export const connectInfiniteCalendarSeries = () =>
    connect(
        mapStateToProps,
        mapDispatchToProps,
        mergeProps,
    )(InfiniteCalendarSeries);

export const ConnectedInfiniteCalendarSeries = connectInfiniteCalendarSeries();
